/**
 * Redux slice for the Document Library feature (MYD-282).
 *
 * Backs the project Document tab, the upload dialog, the preview /
 * version-history dialogs, and the share dialog. Keeps:
 *   - documents (entities/ids, populated with currentVersion)
 *   - versions per document
 *   - share links per document
 *   - per-file upload progress (keyed by a caller-supplied progressKey)
 *
 * Thunks delegate axios calls to libraryDocumentService — keeps URL
 * strings and request shaping out of the slice.
 */
import {
	createAsyncThunk,
	createSlice,
	PayloadAction,
} from '@reduxjs/toolkit'
import type { AxiosRequestConfig } from 'axios'
import * as service from 'features/DocumentLibrary/services/libraryDocumentService'
import {
	matchesScope,
	scopeKey,
	type DocumentLibraryState,
	type DocumentShareLink,
	type InlineUrlsResponse,
	type LibraryDocument,
	type LibraryDocumentVersion,
	type LibraryScopeArgs,
	type CreateShareLinkResponse,
	type RevokeShareLinkResponse,
} from 'features/DocumentLibrary/types/documentLibrary.types'

const SLICE = 'documentLibrary'

const initialState: DocumentLibraryState = {
	entities: {},
	ids: [],
	versionsByDocument: {},
	sharesByDocument: {},
	inlineUrlsByDocument: {},
	uploadProgress: {},
	initializedScopes: [],
	isLoading: false,
	error: null,
}

// Build axios config from the auth slice. Loose typing matches the
// rest of the codebase — getState is untyped on the broader store.
const prepConfig = (getState: any): AxiosRequestConfig => {
	const { userLogin } = getState()
	const token = userLogin?.userInfo?.token
	return token ? { headers: { Authorization: `Bearer ${token}` } } : {}
}

const errorMessage = (err: any): string =>
	err?.response?.data?.message ||
	err?.response?.data ||
	err?.message ||
	'Unknown error'

// ---------------- Thunks ----------------

export const initializeForScope = createAsyncThunk<
	{ scopeArgs: LibraryScopeArgs; documents: LibraryDocument[] },
	LibraryScopeArgs,
	{ rejectValue: string }
>(`${SLICE}/initializeForScope`, async (scopeArgs, { getState, rejectWithValue }) => {
	try {
		const documents = await service.listForScope(scopeArgs, prepConfig(getState))
		return { scopeArgs, documents }
	} catch (err) {
		return rejectWithValue(errorMessage(err))
	}
})

export const fetchOne = createAsyncThunk<
	LibraryDocument,
	{ id: string },
	{ rejectValue: string }
>(`${SLICE}/fetchOne`, async ({ id }, { getState, rejectWithValue }) => {
	try {
		return await service.getOne(id, prepConfig(getState))
	} catch (err) {
		return rejectWithValue(errorMessage(err))
	}
})

export const fetchVersions = createAsyncThunk<
	{ id: string; versions: LibraryDocumentVersion[] },
	{ id: string },
	{ rejectValue: string }
>(`${SLICE}/fetchVersions`, async ({ id }, { getState, rejectWithValue }) => {
	try {
		const versions = await service.listVersions(id, prepConfig(getState))
		return { id, versions }
	} catch (err) {
		return rejectWithValue(errorMessage(err))
	}
})

export const fetchInlineUrls = createAsyncThunk<
	{ id: string; response: InlineUrlsResponse; fetchedAt: number },
	{ id: string },
	{ rejectValue: string }
>(`${SLICE}/fetchInlineUrls`, async ({ id }, { getState, rejectWithValue }) => {
	try {
		const response = await service.getInlineUrls(id, prepConfig(getState))
		return { id, response, fetchedAt: Date.now() }
	} catch (err) {
		return rejectWithValue(errorMessage(err))
	}
})

export interface UploadDocumentArg {
	scopeArgs: LibraryScopeArgs
	file: File
	libraryName?: string
	changeNote?: string
	/** Caller-supplied id used as the key in `uploadProgress`. */
	progressKey?: string
}

export const uploadDocument = createAsyncThunk<
	LibraryDocument,
	UploadDocumentArg,
	{ rejectValue: string }
>(`${SLICE}/uploadDocument`, async (arg, { dispatch, getState, rejectWithValue }) => {
	const progressKey = arg.progressKey
	try {
		const doc = await service.uploadDocument(
			{
				scopeArgs: arg.scopeArgs,
				file: arg.file,
				libraryName: arg.libraryName,
				changeNote: arg.changeNote,
				onProgress: progressKey
					? p => dispatch(setUploadProgress({ key: progressKey, percent: p }))
					: undefined,
			},
			prepConfig(getState),
		)
		if (progressKey) dispatch(clearUploadProgress(progressKey))
		return doc
	} catch (err) {
		if (progressKey) dispatch(clearUploadProgress(progressKey))
		return rejectWithValue(errorMessage(err))
	}
})

export interface UploadVersionArg {
	id: string
	file: File
	changeNote: string
	progressKey?: string
}

export const uploadVersion = createAsyncThunk<
	LibraryDocument,
	UploadVersionArg,
	{ rejectValue: string }
>(`${SLICE}/uploadVersion`, async (arg, { dispatch, getState, rejectWithValue }) => {
	const progressKey = arg.progressKey
	try {
		const doc = await service.uploadVersion(
			{
				id: arg.id,
				file: arg.file,
				changeNote: arg.changeNote,
				onProgress: progressKey
					? p => dispatch(setUploadProgress({ key: progressKey, percent: p }))
					: undefined,
			},
			prepConfig(getState),
		)
		if (progressKey) dispatch(clearUploadProgress(progressKey))
		return doc
	} catch (err) {
		if (progressKey) dispatch(clearUploadProgress(progressKey))
		return rejectWithValue(errorMessage(err))
	}
})

export const renameDocument = createAsyncThunk<
	LibraryDocument,
	{ id: string; libraryName: string },
	{ rejectValue: string }
>(`${SLICE}/renameDocument`, async (arg, { getState, rejectWithValue }) => {
	try {
		return await service.renameDocument(arg, prepConfig(getState))
	} catch (err) {
		return rejectWithValue(errorMessage(err))
	}
})

export const softDeleteDocument = createAsyncThunk<
	{ id: string },
	{ id: string },
	{ rejectValue: string }
>(`${SLICE}/softDeleteDocument`, async ({ id }, { getState, rejectWithValue }) => {
	try {
		await service.softDeleteDocument(id, prepConfig(getState))
		return { id }
	} catch (err) {
		return rejectWithValue(errorMessage(err))
	}
})

export const softDeleteVersion = createAsyncThunk<
	{ id: string; versionId: string },
	{ id: string; versionId: string },
	{ rejectValue: string }
>(`${SLICE}/softDeleteVersion`, async (arg, { getState, rejectWithValue }) => {
	try {
		await service.softDeleteVersion(arg, prepConfig(getState))
		return arg
	} catch (err) {
		return rejectWithValue(errorMessage(err))
	}
})

export interface CreateShareLinkArg {
	documentId: string
	versionId?: string
	ttlDays?: number
	email?: string
	message?: string
}

export const createShareLink = createAsyncThunk<
	CreateShareLinkResponse,
	CreateShareLinkArg,
	{ rejectValue: string }
>(`${SLICE}/createShareLink`, async (arg, { getState, rejectWithValue }) => {
	try {
		return await service.createShareLink(arg, prepConfig(getState))
	} catch (err) {
		return rejectWithValue(errorMessage(err))
	}
})

export const revokeShareLink = createAsyncThunk<
	RevokeShareLinkResponse,
	{ token: string; documentId: string },
	{ rejectValue: string }
>(`${SLICE}/revokeShareLink`, async ({ token }, { getState, rejectWithValue }) => {
	try {
		return await service.revokeShareLink(token, prepConfig(getState))
	} catch (err) {
		return rejectWithValue(errorMessage(err))
	}
})

export const fetchShareLinks = createAsyncThunk<
	{ documentId: string; links: DocumentShareLink[] },
	{ documentId: string },
	{ rejectValue: string }
>(`${SLICE}/fetchShareLinks`, async ({ documentId }, { getState, rejectWithValue }) => {
	try {
		const links = await service.listShareLinks(documentId, prepConfig(getState))
		return { documentId, links }
	} catch (err) {
		return rejectWithValue(errorMessage(err))
	}
})

// ---------------- Slice ----------------

const documentLibrarySlice = createSlice({
	name: SLICE,
	initialState,
	reducers: {
		setUploadProgress(
			state,
			action: PayloadAction<{ key: string; percent: number }>,
		) {
			state.uploadProgress[action.payload.key] = action.payload.percent
		},
		clearUploadProgress(state, action: PayloadAction<string>) {
			delete state.uploadProgress[action.payload]
		},
		clearError(state) {
			state.error = null
		},
	},
	extraReducers: builder => {
		// initializeForScope
		builder
			.addCase(initializeForScope.pending, state => {
				state.isLoading = true
				state.error = null
			})
			.addCase(initializeForScope.fulfilled, (state, action) => {
				const { scopeArgs, documents } = action.payload
				// my-library spans every scope the user has access to —
				// matchesScope can't predicate it (server-side access set
				// not reproducible client-side). Replace `entities` with
				// just the response; other scope views refetch on
				// navigation, so cross-scope visits stay correct.
				const existingForOtherScopes =
					scopeArgs.scope === 'my-library'
						? []
						: state.ids.filter(
								id => !matchesScope(state.entities[id], scopeArgs),
						  )
				const nextIds: string[] = []
				const nextEntities: Record<string, LibraryDocument> = {}
				for (const id of existingForOtherScopes) {
					nextIds.push(id)
					nextEntities[id] = state.entities[id]
				}
				for (const doc of documents) {
					nextEntities[doc._id] = doc
					nextIds.push(doc._id)
				}
				state.entities = nextEntities
				state.ids = nextIds
				const key = scopeKey(scopeArgs)
				if (!state.initializedScopes.includes(key)) {
					state.initializedScopes.push(key)
				}
				state.isLoading = false
			})
			.addCase(initializeForScope.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload || 'Failed to load documents'
			})

		// fetchOne
		builder
			.addCase(fetchOne.fulfilled, (state, action) => {
				const doc = action.payload
				state.entities[doc._id] = doc
				if (!state.ids.includes(doc._id)) state.ids.push(doc._id)
			})
			.addCase(fetchOne.rejected, (state, action) => {
				state.error = action.payload || 'Failed to fetch document'
			})

		// fetchVersions
		builder
			.addCase(fetchVersions.fulfilled, (state, action) => {
				state.versionsByDocument[action.payload.id] = action.payload.versions
			})
			.addCase(fetchVersions.rejected, (state, action) => {
				state.error = action.payload || 'Failed to fetch versions'
			})

		// fetchInlineUrls
		builder
			.addCase(fetchInlineUrls.fulfilled, (state, action) => {
				const { id, response, fetchedAt } = action.payload
				state.inlineUrlsByDocument[id] = { response, fetchedAt }
			})
			.addCase(fetchInlineUrls.rejected, (state, action) => {
				state.error = action.payload || 'Failed to fetch preview URLs'
			})

		// uploadDocument
		builder
			.addCase(uploadDocument.fulfilled, (state, action) => {
				const doc = action.payload
				state.entities[doc._id] = doc
				if (!state.ids.includes(doc._id)) state.ids.unshift(doc._id)
			})
			.addCase(uploadDocument.rejected, (state, action) => {
				state.error = action.payload || 'Upload failed'
			})

		// uploadVersion
		builder
			.addCase(uploadVersion.fulfilled, (state, action) => {
				const doc = action.payload
				state.entities[doc._id] = doc
				// Drop cached versions so VersionHistoryDialog refetches the
				// up-to-date list (with the freshly-flipped isCurrent flags).
				delete state.versionsByDocument[doc._id]
				// Inline URLs point at the previous version's storage keys —
				// invalidate so the preview dialog refetches against the new
				// current version.
				delete state.inlineUrlsByDocument[doc._id]
			})
			.addCase(uploadVersion.rejected, (state, action) => {
				state.error = action.payload || 'New version upload failed'
			})

		// renameDocument
		builder
			.addCase(renameDocument.fulfilled, (state, action) => {
				const doc = action.payload
				state.entities[doc._id] = doc
			})
			.addCase(renameDocument.rejected, (state, action) => {
				state.error = action.payload || 'Rename failed'
			})

		// softDeleteDocument
		builder
			.addCase(softDeleteDocument.fulfilled, (state, action) => {
				const { id } = action.payload
				delete state.entities[id]
				state.ids = state.ids.filter(x => x !== id)
				delete state.versionsByDocument[id]
				delete state.sharesByDocument[id]
				delete state.inlineUrlsByDocument[id]
			})
			.addCase(softDeleteDocument.rejected, (state, action) => {
				state.error = action.payload || 'Delete failed'
			})

		// softDeleteVersion
		builder
			.addCase(softDeleteVersion.fulfilled, (state, action) => {
				const { id, versionId } = action.payload
				const list = state.versionsByDocument[id]
				if (list) {
					state.versionsByDocument[id] = list.filter(v => v._id !== versionId)
				}
			})
			.addCase(softDeleteVersion.rejected, (state, action) => {
				state.error = action.payload || 'Version delete failed'
			})

		// createShareLink — store-level cache is keyed by documentId; refresh
		// via fetchShareLinks once the dialog is open (cheap).
		builder.addCase(createShareLink.rejected, (state, action) => {
			state.error = action.payload || 'Could not create share link'
		})

		// revokeShareLink
		builder
			.addCase(revokeShareLink.fulfilled, (state, action) => {
				const { token, revokedAt } = action.payload
				const docId = action.meta.arg.documentId
				const list = state.sharesByDocument[docId]
				if (list) {
					state.sharesByDocument[docId] = list.map(link =>
						link.token === token ? { ...link, revokedAt } : link,
					)
				}
			})
			.addCase(revokeShareLink.rejected, (state, action) => {
				state.error = action.payload || 'Could not revoke share link'
			})

		// fetchShareLinks
		builder
			.addCase(fetchShareLinks.fulfilled, (state, action) => {
				state.sharesByDocument[action.payload.documentId] = action.payload.links
			})
			.addCase(fetchShareLinks.rejected, (state, action) => {
				state.error = action.payload || 'Failed to fetch share links'
			})
	},
})

export const { setUploadProgress, clearUploadProgress, clearError } =
	documentLibrarySlice.actions

export default documentLibrarySlice
